import { sortBy } from "lodash";
import {
  MeetingViewMeetingNodeNewPageFragmentFragment,
  TopicNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import { ignoredParticipantEmails } from "@helpers/constants";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
} from "@helpers/helpers";

import IndividualNotes from "./individual-notes";

const IndividualNotesContainer = ({
  topic,
  meeting,
  className = "",
  onModEnter,
  isTopicInteracting,
  onWebsocketMessageChange,
  occurrence,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  className?: string;
  onModEnter?: () => void;
  isTopicInteracting?: boolean;
  onWebsocketMessageChange?: (key: string, message: string | null) => void;
  occurrence?: MeetingViewMeetingNodeNewPageFragmentFragment["agendaTopics"][number];
}) => {
  const currentUser = currentUserVar();

  const individualNotes = assertEdgesNonNull(topic.individualNotes);
  const enabledNotes = individualNotes.filter(
    (individualNote) => individualNote.enabled,
  );
  const participantIdsWithNotes = enabledNotes.map(
    (individualNote) => individualNote.creator.id,
  );

  const meetingGroup = meeting.meetingGroup;
  if (!meetingGroup) return null;

  const participants = assertEdgesNonNullWithStringId(meeting.participants);
  const visibleParticipants = sortBy(
    participants,
    (participant) => participant.user?.id !== currentUser.id, // put current user first.
  ).filter((participant) => {
    if (!participant.user?.id) return false;
    if (!participant.participantEmail) return false;
    if (ignoredParticipantEmails.includes(participant.participantEmail)) {
      return false;
    }
    const isCurrentUser = participant.user.id === currentUser.id;
    const hasEnabledNote = participantIdsWithNotes.includes(
      participant.user.id,
    );
    // Include this participant's notes slot when:
    // - their individual note row is explicitly enabled
    // - the topic has individual notes turned on for everyone
    // - they're the current user and the per-user toggle is on
    return (
      hasEnabledNote ||
      topic.includesIndividualNotes ||
      (isCurrentUser && topic.includesIndividualNotesForCurrentUser)
    );
  });

  return (
    <div className={classNames("flex flex-col empty:hidden", className)}>
      {visibleParticipants.map((participant) =>
        !participant.user ? null : (
          <IndividualNotes
            key={participant.id}
            participant={participant}
            topic={topic}
            meeting={meeting}
            onModEnter={onModEnter}
            isTopicInteracting={isTopicInteracting}
            onWebsocketMessageChange={onWebsocketMessageChange}
            occurrence={occurrence}
          />
        ),
      )}
    </div>
  );
};

export default IndividualNotesContainer;
